// Chrome composites on the CPU whenever there is no GPU to composite on — no
// card, a blocklisted driver, a VM, a remote desktop session. WebGL falls back
// to a software renderer at the same time, so its renderer string is a usable
// stand-in for "is the compositor on the GPU".
const SOFTWARE_RENDERERS = /swiftshader|software|llvmpipe|basic render/i;

function probe(): boolean {
    // Nothing to composite in a build step or under jsdom, and nothing worth
    // asking about in a browser that can't blur a backdrop anyway. Both are
    // checked before the canvas below, which jsdom would otherwise complain
    // about at some length.
    if (
        typeof document === "undefined" ||
        typeof CSS === "undefined" ||
        !CSS.supports("backdrop-filter", "blur(3px)")
    ) {
        return false;
    }

    const gl = document.createElement("canvas").getContext("webgl");
    if (!gl) {
        return false;
    }

    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer: unknown = info
        ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL)
        : gl.getParameter(gl.RENDERER);

    // Release the context rather than waiting for the canvas to be collected —
    // browsers only allow a handful of them at a time.
    gl.getExtension("WEBGL_lose_context")?.loseContext();

    return typeof renderer === "string" && !SOFTWARE_RENDERERS.test(renderer);
}

let cached: boolean | undefined;

/**
 * Whether the browser can composite on a GPU — creating a throwaway WebGL
 * context the first time, then remembering the answer.
 *
 * Use it to skip effects that a software compositor cannot afford, and treat an
 * unrecognised renderer as software: a missed effect is cheaper than a dialog
 * that stutters. `styles/backdrop.css.ts` gates the overlay blur on it.
 */
export function hasHardwareCompositing(): boolean {
    cached ??= probe();
    return cached;
}
